import type { StockSurplusLimitation } from '@erp/rpc';
import type { useGT } from 'gt-react';
import { traceReason } from '../../demand-forecasting/surplus/compositionText';
import { customerSalesReasonLabel } from './panelFormat';

type Translate = ReturnType<typeof useGT>;

const LIMITATION_LABELS: Record<
  StockSurplusLimitation,
  (gt: Translate) => string
> = {
  cross_location_quantity_unit_unverified: (gt) =>
    traceReason('cross_location_quantity_unit_unverified', gt),
  aggregate_numeric_overflow: (gt) =>
    traceReason('aggregate_numeric_overflow', gt),
  customer_sales_coverage_unverified: (gt) =>
    gt(
      'Customer-sales coverage is not verified. Recorded sales are context only: they cannot establish fully covered months, so no annual sales basis or estimate is made from them.',
      { context: 'stock surplus limitation' },
    ),
  quantity_unit_unverified: (gt) =>
    gt(
      'Stock and sales quantity units must be confirmed before surplus can be calculated.',
      { context: 'stock surplus limitation' },
    ),
  current_live_diagnostic: (gt) =>
    gt(
      'Uses current inventory, commitments and minimum protection, even when viewing an older forecast. This is not permission to transfer stock.',
      { context: 'stock surplus limitation' },
    ),
  engine_commitment_limitations: (gt) =>
    gt(
      'Existing commitment rules exclude sales orders older than 30 days, use ordered quantities for partial shipments, and may lag after a transfer is acknowledged.',
      { context: 'stock surplus limitation' },
    ),
  source_overlap_conflict: (gt) =>
    customerSalesReasonLabel('source_overlap_conflict', gt),
  missing_source_identity: (gt) =>
    customerSalesReasonLabel('missing_source_identity', gt),
  source_incompatible: (gt) =>
    customerSalesReasonLabel('source_incompatible', gt),
  restricted_catchment: (gt) =>
    gt(
      'Part of the demand catchment is outside your location access, so surplus is not calculated where those sales would be retained.',
      { context: 'stock surplus limitation' },
    ),
};

export function stockSurplusLimitationLabel(
  limitation: StockSurplusLimitation,
  gt: Translate,
): string {
  return LIMITATION_LABELS[limitation](gt);
}
